import React, { type ReactNode, useEffect, useMemo, useState } from 'react';
import { IntlProvider } from 'react-intl';
import { normalizeLocale, DEFAULT_LOCALE } from '../i18n/locale';

export type I18nProviderProps = {
	children: ReactNode;
	fetchTranslations: (locale: string) => Promise<Record<string, string>>;
	locale?: string;
};

export const I18nProvider = ({
	children,
	locale: incomingLocale = DEFAULT_LOCALE,
	fetchTranslations,
}: I18nProviderProps): React.JSX.Element | null => {
	const [messages, setMessages] = useState<Record<string, string>>();

	const locale = useMemo(() => normalizeLocale(incomingLocale), [incomingLocale]);

	useEffect(() => {
		const loadTranslations = async () => {
			const localeMessages = await fetchTranslations(locale);
			setMessages(localeMessages);
		};
		void loadTranslations();
	}, [fetchTranslations, locale]);

	if (!messages) {
		return null;
	}

	// Do not place formatMessage / FormattedMessage / useIntl calls here outside of the I18nProvider.
	// They will not function correctly due to a lack of I18n context.

	return (
		<IntlProvider locale={locale} messages={messages} defaultLocale={DEFAULT_LOCALE}>
			{children}
		</IntlProvider>
	);
};
